import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router';
import { usePrefixedNavigate as useNavigate } from '../../../../src/hooks/usePrefixedNavigate';
import { 
  ProjectDetailTemplate, 
  ProjectDetailTemplateProps, 
  DetailProject 
} from '@edusense/project-ui';
import { useAuth } from '@edusense/shared-auth-core';
import { transformProject } from '@edusense/entities-project';
import { 
  useGetProjectById, 
  useDeleteProject,
  ProjectResponse as ApiProjectResponse 
} from '@edusense/edusense-project-client';
import { Project } from '@edusense/project-types';
import { StatusDisplay } from "@edusense/shared-ui-kit";
// Paraglide JS 메시지 import
import {
  common_loading,
  common_error,
  common_confirm,
  common_delete,
  common_success,
  project_id_invalid,
  project_fetch_failed,
  project_data_transform_error,
  project_delete_confirm,
  project_delete_success,
  project_delete_error,
  project_duplicate_todo,
  project_duplicate_error,
  project_report_todo,
  project_view_code_todo,
  project_run_code_todo,
  project_loading,
  project_empty
} from '@paraglide/messages';

export interface ProjectDetailPageProps {}

/**
 * 프로젝트 상세 페이지 컴포넌트
 * 
 * 프로젝트 ID를 URL 파라미터로부터 받아 해당 프로젝트의 상세 정보를 표시합니다.
 * ProjectDetailTemplate을 사용하여 UI를 렌더링합니다.
 */
const ProjectDetailPage: React.FC<ProjectDetailPageProps> = () => {
  const { projectId: id } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const commentSectionRef = useRef<HTMLDivElement>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  // 🚀 자동 생성된 React Query 훅으로 완전 자동화!
  const {
    data: apiProjectData,
    isLoading: loading,
    error: queryError,
    refetch
  } = useGetProjectById(
    { id: id! },
    { 
      query: { 
        enabled: !!id,
        staleTime: 1000 * 60 * 5, // 5분
        retry: 1
      }
    }
  );

  // 🚀 프로젝트 삭제 mutation 훅
  const deleteProjectMutation = useDeleteProject();

  // 🎯 자동으로 변환된 프로젝트 데이터 (memoized)
  const project: Project | null = React.useMemo(() => {
    if (!apiProjectData?.data) return null;
    try {
      return transformProject(apiProjectData.data as ApiProjectResponse);
    } catch (transformError) {
      console.error('[ProjectDetailPage] 프로젝트 데이터 변환 실패:', transformError);
      return null;
    }
  }, [apiProjectData]);

  // 🎯 에러 메시지 처리 (자동화)
  const error = React.useMemo(() => {
    if (!id) return project_id_invalid();
    if (queryError) return queryError instanceof Error ? queryError.message : project_fetch_failed();
    if (apiProjectData?.data && !project) return project_data_transform_error();
    return null;
  }, [id, queryError, apiProjectData, project]);

  // 🚀 복잡한 useEffect 완전 제거! React Query가 모든 것을 자동 처리

  const handleBack = () => {
    navigate(-1);
  };
  
  const handleComment = (id: string) => {
    console.log(`댓글 섹션으로 스크롤 및 새로고침 트리거: ${id}`);
    commentSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
    setRefetchTrigger(prev => prev + 1);
    refetch(); // 🚀 React Query refetch로 간단히!
  };
  
  const handleShare = (id: string) => {
    console.log(`공유 모달 열기: ${id}`);
  };
  
  const isAuthor = isAuthenticated && project?.author?.id === user?.id;

  const handleEdit = (id: string) => {
    if (!isAuthor) return;
    console.log(`프로젝트 편집 페이지로 이동: ${id}`);
    navigate(`/projects/${id}/edit`);
  };
  
  // 🚀 자동화된 삭제 처리 - mutation 훅 사용!
  const handleDelete = async (id: string) => {
    if (!isAuthor) return;
    console.log(`프로젝트 삭제: ${id}`);
    if (window.confirm(project_delete_confirm())) {
      try {
        await deleteProjectMutation.mutateAsync({ id: String(id) });
        alert(project_delete_success());
        navigate('/projects');
      } catch (err) {
        console.error("Error deleting project:", err);
        alert(project_delete_error());
      }
    }
  };
  
  const handleDuplicate = async (id: string) => {
    console.log(`프로젝트 복제: ${id}`);
    try {
      alert(project_duplicate_todo());
    } catch (err) {
      console.error("Error duplicating project:", err);
      alert(project_duplicate_error());
    }
  };
  
  const handleReport = (id: string) => {
    if (!isAuthenticated) { console.log("Login required for report"); return; }
    console.log(`신고 모달 열기: ${id}`);
    alert(project_report_todo());
  };
  
  const handleDownload = (id: string) => {
    console.log(`프로젝트 다운로드: ${id}`);
  };
  
  const handleViewCode = (id: string) => {
    console.log(`코드 보기: ${id}`);
    alert(project_view_code_todo());
  };
  
  const handleRunCode = (id: string) => {
    console.log(`코드 실행: ${id}`);
    alert(project_run_code_todo());
  };

  if (loading) {
    return <StatusDisplay type="loading" message={project_loading()} className="p-8" />;
  }

  if (error) {
    return <StatusDisplay type="error" message={error} className="p-8" />;
  }

  if (!project) {
    return <StatusDisplay type="empty" message={project_empty()} className="p-8" />;
  }
  
  return (
    <ProjectDetailTemplate
      project={project as DetailProject}
      onBack={handleBack}
      onComment={() => handleComment(project.id)}
      onShare={() => handleShare(project.id)}
      onEdit={isAuthor ? () => handleEdit(project.id) : undefined}
      onDelete={isAuthor ? () => handleDelete(project.id) : undefined}
      onDuplicate={() => handleDuplicate(project.id)}
      onReport={isAuthenticated ? () => handleReport(project.id) : undefined}
      onDownload={() => handleDownload(project.id)}
      onViewCode={() => handleViewCode(project.id)}
      commentSectionRef={commentSectionRef}
      refetchTrigger={refetchTrigger}
    />
  );
};

export default ProjectDetailPage; 